import { ImmutableVector3 } from '@modugen/ui-toolkit'

/**
 * Converts storey heights to storey boundaries
 * @param storeyHeights Array of heights for each storey
 * @returns StoreyBoundaries object with storey indices as keys and [lower, upper] tuples as values
 */
export const fromStoreyHeights = (storeyHeights: number[]): StoreyBoundaries => {
  let cumsum = 0
  const storeyBoundaries: StoreyBoundaries = {}

  for (let idx = 0; idx < storeyHeights.length; idx++) {
    const h = storeyHeights[idx]
    const lower = cumsum
    const upper = cumsum + h
    storeyBoundaries[idx.toString()] = [lower, upper]
    cumsum = upper
  }

  return storeyBoundaries
}

const DEFAULT_FOUNDATION_HALF_WIDTH = 30

/**
 * Creates a default foundation
 * @returns A Foundation object
 */
const createDefaultFoundation = (): Foundation => {
  return {
    guid: 'foundation-replacement',
    element_guid: 'foundation-replacement',
    element_type: 'foundation',
    label: 'BP01',
    storey: '0',
    shape: {
      points: [
        new ImmutableVector3(-DEFAULT_FOUNDATION_HALF_WIDTH, -DEFAULT_FOUNDATION_HALF_WIDTH, 0),
        new ImmutableVector3(DEFAULT_FOUNDATION_HALF_WIDTH, -DEFAULT_FOUNDATION_HALF_WIDTH, 0),
        new ImmutableVector3(DEFAULT_FOUNDATION_HALF_WIDTH, DEFAULT_FOUNDATION_HALF_WIDTH, 0),
        new ImmutableVector3(-DEFAULT_FOUNDATION_HALF_WIDTH, DEFAULT_FOUNDATION_HALF_WIDTH, 0),
      ],
    },
  } as Foundation
}

/**
 * Creates an empty model from storey heights
 * @param storeyHeights Array of heights for each storey
 * @returns A PlanarModel with storey boundaries and no structural elements
 */
export const createEmptyModel = (storeyHeights: number[]): PlanarModel => {
  if (storeyHeights.length === 0) {
    throw new Error('At least one storey height must be provided')
  }

  const storeyBoundaries = fromStoreyHeights(storeyHeights)

  const model: PlanarModel = {
    walls: [],
    slabs: [],
    roof_slabs: [],
    vertical_slabs: [],
    vertical_roof_slabs: [],
    beams: [],
    purlins: [],
    columns: [],
    rips: [],
    lintels: [],
    foundationElements: [createDefaultFoundation()],
    storey_boundaries: storeyBoundaries,
  }

  return model
}
